import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import {
  FaBoxes,
  FaCertificate,
  FaChartLine,
  FaExclamationTriangle,
  FaGlobeAfrica,
  FaIndustry,
  FaMapMarkedAlt,
  FaSyncAlt,
} from "react-icons/fa";
import CartographyFilters from "./CartographyFilters";
import CartographyKpiCard from "./CartographyKpiCard";
import CartographyMap from "./CartographyMap";
import { backendUrl, enrichSupplier, normalizeText } from "./cartographyUtils";
import SectorStatsTable from "./SectorStatsTable";
import SuppliersCartographyTable from "./SuppliersCartographyTable";
import {
  CartographyFiltersState,
  CartographySupplier,
  ProductRaw,
  SupplierRaw,
} from "./types";

const initialFilters: CartographyFiltersState = {
  search: "",
  wilaya: "",
  sector: "",
  pyramid: "",
  exportStatus: "",
  homologation: "",
};

const NationalCartographyView: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState<CartographySupplier[]>([]);
  const [filters, setFilters] =
    useState<CartographyFiltersState>(initialFilters);

  const updateFilter = (key: keyof CartographyFiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const supplierRes = await fetch(`${backendUrl}/api/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPage: 1,
          limitValue: 10000,
          search: "",
          column: "id_supplier",
          sort: "DESC",
        }),
      });

      const supplierData: SupplierRaw[] = await supplierRes.json();
      const enriched = await Promise.all(
        supplierData.map(async (supplier) => {
          let products: ProductRaw[] = [];
          try {
            if (supplier.id_user) {
              const productRes = await fetch(
                `${backendUrl}/api/supplier-products/${supplier.id_user}`,
              );
              products = await productRes.json();
            }
          } catch {
            products = [];
          }
          return enrichSupplier(
            supplier,
            Array.isArray(products) ? products : [],
          );
        }),
      );

      setSuppliers(
        enriched.filter((supplier) => {
          const category = normalizeText(supplier.aida_category).toLowerCase();
          const reason = normalizeText(
            supplier.aida_out_of_scope_reason,
          ).toLowerCase();
          return (
            Number(supplier.aida_out_of_scope || 0) !== 1 &&
            category !== "hors_perimetre_importateur" &&
            !reason.includes("importateur") &&
            !reason.includes("distributeur")
          );
        }),
      );
    } catch (error) {
      console.error("Erreur chargement cartographie nationale:", error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  const wilayas = useMemo(
    () =>
      Array.from(
        new Set(suppliers.map((s) => normalizeText(s.wilaya)).filter(Boolean)),
      ).sort(),
    [suppliers],
  );
  const sectors = useMemo(
    () =>
      Array.from(
        new Set(
          suppliers.map((s) => normalizeText(s.mainSector)).filter(Boolean),
        ),
      ).sort(),
    [suppliers],
  );
  const pyramids = useMemo(
    () =>
      Array.from(
        new Set(
          suppliers.map((s) => normalizeText(s.pyramid_level)).filter(Boolean),
        ),
      ).sort(),
    [suppliers],
  );
  const homologations = useMemo(
    () =>
      Array.from(
        new Set(
          suppliers
            .map((s) => normalizeText(s.homologation_status))
            .filter(Boolean),
        ),
      ).sort(),
    [suppliers],
  );

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((supplier) => {
      const supplierSearch =
        `${supplier.legal_name} ${supplier.trade_name || ""} ${supplier.wilaya || ""} ${supplier.mainSector || ""} ${supplier.aida_category || ""} ${supplier.aida_badge || ""}`.toLowerCase();
      const matchesSearch =
        !filters.search ||
        supplierSearch.includes(filters.search.toLowerCase());
      const matchesWilaya =
        !filters.wilaya || supplier.wilaya === filters.wilaya;
      const matchesSector =
        !filters.sector || supplier.mainSector === filters.sector;
      const matchesPyramid =
        !filters.pyramid || supplier.pyramid_level === filters.pyramid;
      const matchesHomologation =
        !filters.homologation ||
        supplier.homologation_status === filters.homologation ||
        supplier.products.some(
          (p) => p.homologation_status === filters.homologation,
        );
      const matchesExport =
        !filters.exportStatus ||
        supplier.export_potential === filters.exportStatus ||
        supplier.products.some((p) => p.export_status === filters.exportStatus);
      return (
        matchesSearch &&
        matchesWilaya &&
        matchesSector &&
        matchesPyramid &&
        matchesHomologation &&
        matchesExport
      );
    });
  }, [suppliers, filters]);

  const kpis = useMemo(() => {
    const totalCapacity = filteredSuppliers.reduce(
      (sum, s) => sum + s.totalCapacity,
      0,
    );
    const requested = filteredSuppliers.reduce(
      (sum, s) => sum + s.requestedCapacity,
      0,
    );
    return {
      suppliers: filteredSuppliers.length,
      wilayas: new Set(
        filteredSuppliers.map((s) => normalizeText(s.wilaya)).filter(Boolean),
      ).size,
      sectors: new Set(
        filteredSuppliers
          .map((s) => normalizeText(s.mainSector))
          .filter(Boolean),
      ).size,
      products: filteredSuppliers.reduce(
        (sum, s) => sum + s.products.length,
        0,
      ),
      alerts: filteredSuppliers.reduce(
        (sum, s) => sum + s.nonCapacitaryProducts,
        0,
      ),
      exportReady: filteredSuppliers.filter(
        (s) =>
          s.exportableProducts > 0 ||
          normalizeText(s.export_potential) === "Export-ready",
      ).length,
      substitution: filteredSuppliers.reduce(
        (sum, s) => sum + s.substitutionProducts,
        0,
      ),
      coverage:
        requested > 0 ? Math.round((totalCapacity / requested) * 100) : 0,
      totalCapacity,
    };
  }, [filteredSuppliers]);

  return (
    <div className="aida-cartography-shell">
      <div className="aida-hero-algeria">
        <div className="aida-hero-flag" aria-label="Algérie">
          <img
            src="/asset/images/Flag_of_Algeria.svg"
            alt="Drapeau de l'Algérie"
          />
        </div>
        <div className="aida-hero-content">
          <div className="aida-page-eyebrow">
            AIDA • Algérie • Cartographie nationale
          </div>
          <h3>
            <FaMapMarkedAlt /> Carte nationale du tissu industriel automobile
          </h3>
          <p>
            Une lecture géographique et industrielle des sous-traitants
            algériens : localisation, filières, capacités, maturité,
            homologation, export et substitution à l’importation.
          </p>
          <div className="aida-hero-pills">
            <span>🇩🇿 Vision Algérie</span>
            <span>Capacités locales</span>
            <span>Filières industrielles</span>
            <span>Aide à la décision</span>
          </div>
        </div>

        <div className="aida-hero-action">
          <Button variant="warning" onClick={loadSuppliers} disabled={loading}>
            <FaSyncAlt className="me-2" /> Actualiser
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="aida-loading-state">
          <Spinner animation="border" />
          <span>Chargement de la cartographie nationale...</span>
        </div>
      ) : (
        <>
          <Row className="g-4 mb-4 align-items-stretch aida-map-first-row">
            <Col xxl={9} xl={8} lg={12}>
              <Card className="aida-panel-card aida-map-main-card h-100">
                <Card.Body>
                  <div className="aida-section-heading mb-3">
                    <div>
                      <h5>
                        <FaMapMarkedAlt /> Carte géographique nationale
                      </h5>
                      <p>
                        Les marqueurs représentent les fournisseurs. La couleur
                        indique la filière, le rouge signale une alerte de
                        capacité.
                      </p>
                    </div>
                    <div className="aida-live-badge">
                      <span className="aida-live-dot" />{" "}
                      {filteredSuppliers.length} fournisseur(s)
                    </div>
                  </div>
                  <CartographyMap
                    suppliers={filteredSuppliers}
                    sectors={sectors}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col xxl={3} xl={4} lg={12}>
              <div className="aida-side-kpis">
                <CartographyKpiCard
                  icon={<FaIndustry />}
                  title="Sous-traitants"
                  value={kpis.suppliers}
                  subtitle="Fournisseurs recensés"
                  tone="dark"
                />
                <CartographyKpiCard
                  icon={<FaMapMarkedAlt />}
                  title="Wilayas couvertes"
                  value={kpis.wilayas}
                  subtitle="Présence territoriale"
                  tone="green"
                />
                <CartographyKpiCard
                  icon={<FaBoxes />}
                  title="Produits"
                  value={kpis.products}
                  subtitle={`${kpis.sectors} filières actives`}
                  tone="blue"
                />
                <CartographyKpiCard
                  icon={<FaChartLine />}
                  title="Capacité cumulée"
                  value={kpis.totalCapacity}
                  subtitle="Capacité déclarée"
                  tone="yellow"
                />
                <CartographyKpiCard
                  icon={<FaExclamationTriangle />}
                  title="Alertes capacité"
                  value={kpis.alerts}
                  subtitle="Produits non capacitaires"
                  tone="red"
                />
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4 aida-secondary-kpis">
            <Col xl={4} md={6}>
              <CartographyKpiCard
                icon={<FaGlobeAfrica />}
                title="Export"
                value={kpis.exportReady}
                subtitle="Export-ready / potentiel"
                tone="green"
              />
            </Col>
            <Col xl={4} md={6}>
              <CartographyKpiCard
                icon={<FaCertificate />}
                title="Substitution import"
                value={kpis.substitution}
                subtitle="Produits remplaçables localement"
                tone="yellow"
              />
            </Col>
            <Col xl={4} md={12}>
              <CartographyKpiCard
                icon={<FaBoxes />}
                title="Couverture capacitaire"
                value={`${kpis.coverage}%`}
                subtitle="Capacité locale / besoin constructeur"
                tone="blue"
              />
            </Col>
          </Row>

          <CartographyFilters
            search={filters.search}
            selectedWilaya={filters.wilaya}
            selectedSector={filters.sector}
            selectedPyramid={filters.pyramid}
            selectedExport={filters.exportStatus}
            selectedHomologation={filters.homologation}
            wilayas={wilayas}
            sectors={sectors}
            pyramids={pyramids}
            homologations={homologations}
            onSearch={(v) => updateFilter("search", v)}
            onWilaya={(v) => updateFilter("wilaya", v)}
            onSector={(v) => updateFilter("sector", v)}
            onPyramid={(v) => updateFilter("pyramid", v)}
            onExport={(v) => updateFilter("exportStatus", v)}
            onHomologation={(v) => updateFilter("homologation", v)}
            onReset={() => setFilters(initialFilters)}
          />

          <Row className="g-4 mb-4">
            <Col xl={5}>
              <SectorStatsTable suppliers={filteredSuppliers} />
            </Col>
            <Col xl={7}>
              <Card className="aida-panel-card h-100">
                <Card.Body>
                  <div className="aida-section-heading mb-3">
                    <div>
                      <h5>
                        <FaChartLine /> Lecture institutionnelle
                      </h5>
                      <p>
                        Cette vue met en avant les capacités du tissu industriel
                        algérien et les priorités d’accompagnement.
                      </p>
                    </div>
                  </div>
                  <div className="aida-impact-grid">
                    <div>
                      <strong>Objectif</strong>
                      <span>
                        Rendre visible le potentiel national auprès des
                        constructeurs et institutions.
                      </span>
                    </div>
                    <div>
                      <strong>Décision</strong>
                      <span>
                        Identifier les filières fortes, les zones à renforcer et
                        les besoins d’investissement.
                      </span>
                    </div>
                    <div>
                      <strong>Industrie</strong>
                      <span>
                        Suivre la capacité, l’homologation, l’export et la
                        substitution à l’import.
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <SuppliersCartographyTable suppliers={filteredSuppliers} />
        </>
      )}
    </div>
  );
};

export default NationalCartographyView;
